import * as React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';

import apiClient, { shouldRedirectToLogin } from '../services/api-client';
import AppConfig from '../services/app-config';
import AuthService from '../services/auth.js';
import { isReservedTeam } from '../services/systemTeams';
import GestionTypeIcon from '../components/GestionTypeIcon';
import PageHeader from '../components/PageHeader';
import SolarIcon from '../icons/SolarIcon';

// Preset chip colors keyed by estado key. Falls back to a neutral chip
// using the type config's nombre_corto label when no preset exists.
const statusChipColors = {
  'reportado': { bg: '#fff3e0', color: '#e65100' },
  'en_progreso': { bg: '#e3f2fd', color: '#1565c0' },
  'en_revision': { bg: '#e3f2fd', color: '#1565c0' },
  'asignado': { bg: '#e8f5e9', color: '#2e7d32' },
  'programado': { bg: '#ede7f6', color: '#4527a0' },
  'en_espera': { bg: '#f3e5f5', color: '#7b1fa2' },
  'aprobado': { bg: '#e8f5e9', color: '#2e7d32' },
  'rechazado': { bg: '#fbe9e7', color: '#bf360c' },
  'resuelto': { bg: '#e8f5e9', color: '#2e7d32' },
  'completado': { bg: '#e8f5e9', color: '#2e7d32' },
  'cancelado': { bg: '#fbe9e7', color: '#bf360c' },
};

function getStateChip(estadoKey, estados) {
  const preset = statusChipColors[estadoKey];
  const friendly = estados && estados[estadoKey];
  const label = friendly ? friendly.nombre_corto : estadoKey;
  if (preset) {
    return { bg: preset.bg, color: preset.color, label };
  }
  return { bg: '#f5f5f5', color: '#616161', label };
}

function GestionListItem({ gestion, typeConfig, onClick }) {
  const estados = (typeConfig && typeConfig.estados) || {};
  const chipInfo = getStateChip(gestion.estado.estado, estados);
  const equipos = gestion.equipos_asignados || [];
  // Unread comments: comments from other users whose fecha (epoch seconds)
  // is after the last-read timestamp stored in localStorage. If no key exists,
  // treat all incoming comments as unread.
  const comentarios = gestion.comentarios || [];
  const currentEmail = AuthService.getCurrentUserEmail();
  const lastReadRaw = localStorage.getItem(`mgo:gestion-read:${gestion.id}`);
  const lastRead = lastReadRaw ? Number(lastReadRaw) : null;
  const unread = comentarios.filter((c) => {
    if (c.usuario === currentEmail) return false;
    if (lastRead === null) return true;
    return Number(c.fecha) > lastRead;
  }).length;
  return (
    <Card
      sx={{
        mb: 1, borderRadius: '10px', cursor: 'pointer',
        border: '1px solid', borderColor: 'divider', boxShadow: 0,
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
        transition: 'box-shadow 0.2s'
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" component="div">
            Ticket {gestion.id}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3, flex: 1, minWidth: 0 }} noWrap>
              {gestion.titulo}
            </Typography>
            {unread > 0 && (
              <Badge color="error" badgeContent={unread} aria-label="Mensajes sin leer">
                <SolarIcon name="chat-round-dots" size={24} color="secondary.main" />
              </Badge>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" component="div">
              Creado {AppConfig.formatFullDate(gestion.estado.fecha)}
            </Typography>
            <Chip
              label={chipInfo.label}
              size="small"
              sx={{
                height: 20, fontSize: '0.65rem', fontWeight: 700,
                backgroundColor: chipInfo.bg, color: chipInfo.color,
                borderRadius: 1, '& .MuiChip-label': { px: 1 }
              }}
            />
          </Box>
          {equipos.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.4, flexWrap: 'wrap' }}>
              <GroupIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
              {(equipos || []).filter((eq) => !isReservedTeam(eq)).map((eq) => (
                <Chip
                  key={eq}
                  label={eq}
                  size="small"
                  sx={{
                    height: 18, fontSize: '0.66rem', fontWeight: 600,
                    backgroundColor: 'action.hover',
                    color: 'text.secondary',
                    borderRadius: 1, '& .MuiChip-label': { px: 0.8 }
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function GestionesList({ setNavbarState, navbarState }) {
  const { typeKey } = useParams();
  const navigate = useNavigate();
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [loading, setLoading] = React.useState(true);
  const [showBackdrop, setShowBackdrop] = React.useState(false);
  const [tabValue, setTabValue] = React.useState('active');
  const [typeConfig, setTypeConfig] = React.useState(null);
  const [activeGestiones, setActiveGestiones] = React.useState([]);
  const [resolvedGestiones, setResolvedGestiones] = React.useState([]);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [total, setTotal] = React.useState(0);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const [showSearch, setShowSearch] = React.useState(false);
  const searchInputRef = React.useRef(null);
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const PAGE_SIZE = 20;
  const sentinelRef = React.useRef(null);

  const handleSearchChange = (event) => setSearchValue(event.target.value);
  const handleToggleSearch = () => {
    setShowSearch((prev) => {
      const next = !prev;
      if (!next) setSearchValue('');
      return next;
    });
  };
  React.useEffect(() => {
    if (showSearch) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [showSearch]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchTypeConfig = React.useCallback(async () => {
    try {
      const resp = await apiClient.get('gestiones/types', {}, { action: 'fetch_gestion_types' });
      if (resp.status === 200) {
        const types = (resp.data && resp.data.data && resp.data.data.types) || [];
        const cfg = types.find((t) => t.key === typeKey) || null;
        setTypeConfig(cfg);
        return cfg;
      }
    } catch (error) {
      if (shouldRedirectToLogin(error)) {
        setRedirect({ trigger: true, route: '/login' });
      }
      console.error('Error fetching gestion types:', error);
    }
    return null;
  }, [typeKey]);

  const fetchList = React.useCallback((tab, cfg) => {
    setShowBackdrop(true);
    setLoading(true);
    setOffset(PAGE_SIZE);
    const qParam = debouncedSearch ? `&q=${encodeURIComponent(debouncedSearch)}` : '';
    const endpoint = `gestiones/${encodeURIComponent(typeKey)}/${tab}/list?limit=${PAGE_SIZE}&offset=0${qParam}`;
    apiClient.get(endpoint, {}, { action: 'fetch_gestiones_list' })
      .then((response) => {
        if (response.status === 200) {
          const data = (response.data && response.data.data) || {};
          const items = data.items || [];
          if (tab === 'active') {
            setActiveGestiones(items);
          } else {
            setResolvedGestiones(items);
          }
          setHasMore(data.hasMore || false);
          setTotal(data.total || 0);
        }
        setShowBackdrop(false);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching gestiones:', error);
        if (shouldRedirectToLogin(error)) {
          setRedirect({ trigger: true, route: '/login' });
        }
        setShowBackdrop(false);
        setLoading(false);
      });
  }, [typeKey, debouncedSearch]);

  const loadMore = React.useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const currentOffset = offset;
    const qParam = debouncedSearch ? `&q=${encodeURIComponent(debouncedSearch)}` : '';
    const endpoint = `gestiones/${encodeURIComponent(typeKey)}/${tabValue}/list?limit=${PAGE_SIZE}&offset=${currentOffset}${qParam}`;
    apiClient.get(endpoint, {}, { action: 'fetch_gestiones_more' })
      .then((response) => {
        if (response.status === 200) {
          const data = (response.data && response.data.data) || {};
          const newItems = data.items || [];
          if (tabValue === 'active') {
            setActiveGestiones(prev => [...prev, ...newItems]);
          } else {
            setResolvedGestiones(prev => [...prev, ...newItems]);
          }
          setOffset(currentOffset + PAGE_SIZE);
          setHasMore(data.hasMore || false);
        }
        setLoadingMore(false);
      })
      .catch((error) => {
        console.error('Error loading more gestiones:', error);
        setLoadingMore(false);
      });
  }, [typeKey, tabValue, offset, hasMore, loadingMore, debouncedSearch]);

  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
        loadMore();
      }
    }, { rootMargin: '100px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, tabValue, offset, loadMore]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setOffset(0);
    setHasMore(false);
    fetchList(newValue, typeConfig);
  };

  const debounceSkipRef = React.useRef(true);
  React.useEffect(() => {
    if (debounceSkipRef.current) {
      debounceSkipRef.current = false;
      return;
    }
    fetchList(tabValue, typeConfig);
  }, [debouncedSearch]);

  React.useEffect(() => {
    if (setNavbarState && navbarState) {
      setNavbarState({ ...navbarState, showBack: true, showOptions: false, showSearch: false });
    }
    let active = true;
    (async () => {
      const cfg = await fetchTypeConfig();
      if (!active) return;
      fetchList('active', cfg);
    })();
    return () => { active = false; };
  }, [typeKey]);

  const renderEmptyState = (message, description) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 200px)' }}>
      <Box sx={{ textAlign: 'center', px: 3 }}>
        <BuildCircleOutlinedIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{message}</Typography>
        <Typography variant="body2" color="text.secondary">{description}</Typography>
      </Box>
    </Box>
  );

  const renderList = (items) => (
    items.map((g) => (
      <GestionListItem
        key={g.id}
        gestion={g}
        typeConfig={typeConfig}
        onClick={() => setRedirect({ trigger: true, route: `/gestion/${g.id}` })}
      />
    ))
  );

  const headerTitle = (typeConfig && (typeConfig.nombre_plural || typeConfig.nombre)) || 'Gestiones';

  // Only show the "Nueva Gestión" FAB if the current user is allowed to create
  // (creadores_permitidos includes their role). Backend will re-validate.
  const canCreate = (() => {
    if (!typeConfig || !typeConfig.creadores_permitidos) return true;
    const role = AuthService.getCurrentUserRole();
    if (typeConfig.creadores_permitidos.includes(role)) return true;
    if (role === 'admin') {
      // admin:<team> entries — allow any admin; backend enforces team specifics.
      return typeConfig.creadores_permitidos.some((c) => c === 'admin' || c.startsWith('admin:'));
    }
    return false;
  })();

  return (
    <Container maxWidth="sm">
      {redirect.trigger && <Navigate to={redirect.route} replace={true} />}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: '3.2rem' }}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Backdrop open={showBackdrop} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ mt: 0, pb: 10 }}>
        <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.default', pt: 1.5, pb: 0.5 }}>
          <PageHeader title={headerTitle} icon={<GestionTypeIcon typeKey={typeKey} size={26} color="inherit" />} onBack={() => navigate(-1)} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: showSearch ? 1 : 0.5 }}>
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5, flexGrow: 1 }}>
              <Chip
                label="Activos"
                clickable
                color={tabValue === 'active' ? 'primary' : 'default'}
                variant={tabValue === 'active' ? 'filled' : 'outlined'}
                onClick={(e) => handleTabChange(e, 'active')}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                label="Resueltos"
                clickable
                color={tabValue === 'resolved' ? 'primary' : 'default'}
                variant={tabValue === 'resolved' ? 'filled' : 'outlined'}
                onClick={(e) => handleTabChange(e, 'resolved')}
                sx={{ fontWeight: 700 }}
              />
            </Stack>
            <IconButton
              onClick={handleToggleSearch}
              sx={{ width: 40, height: 40, borderRadius: '10px', border: '1px solid', borderColor: 'divider', color: 'text.primary' }}
            >
              <SearchIcon />
            </IconButton>
          </Box>
          {showSearch && (
            <TextField
              fullWidth
              size="small"
              id="search"
              sx={{ textAlign: 'center', mb: 0, mt: 0.5 }}
              label="Buscar"
              variant="outlined"
              value={searchValue}
              onChange={handleSearchChange}
              inputRef={searchInputRef}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleToggleSearch}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                style: { borderRadius: '8px' }
              }}
            />
          )}
        </Box>

        <Box>
          {tabValue === 'resolved' ? (
            !loading && (
              resolvedGestiones.length === 0
                ? (searchValue.trim()
                  ? renderEmptyState('Sin resultados', `No hay gestiones que coincidan con «${searchValue.trim()}».`)
                  : renderEmptyState('No hay gestiones resueltas', 'Aquí podrás ver las gestiones que estén resueltas.'))
                : renderList(resolvedGestiones)
            )
          ) : (
            !loading && (
              activeGestiones.length === 0
                ? (searchValue.trim()
                  ? renderEmptyState('Sin resultados', `No hay gestiones que coincidan con «${searchValue.trim()}».`)
                  : renderEmptyState('No hay gestiones activas', 'Aquí podrás ver las gestiones que estén activas.'))
                : renderList(activeGestiones)
            )
          )}
        </Box>
        {hasMore && (
          <div ref={sentinelRef} style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {loadingMore && <CircularProgress size={24} />}
          </div>
        )}
        {!hasMore && (activeGestiones.length > 0 || resolvedGestiones.length > 0) && (
          <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.75rem', py: 2 }}>
            No hay más registros
          </Typography>
        )}
      </Box>

      {canCreate && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 'calc(84px + env(safe-area-inset-bottom))',
            left: 'calc(var(--app-drawer-width, 0px) + (100vw - var(--app-drawer-width, 0px)) / 2)',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: 600,
            px: 2,
            zIndex: 1300,
            pointerEvents: 'none'
          }}
        >
          <Button
            fullWidth
            variant="contained"
            color="success"
            onClick={() => setRedirect({ trigger: true, route: `/nuevagestion/${typeKey}` })}
            sx={{
              height: 56,
              borderRadius: '10px',
              fontWeight: 800,
              pointerEvents: 'auto'
            }}
          >
            Iniciar gestión
          </Button>
        </Box>
      )}
    </Container>
  );
}
